import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormFieldWrap, Input } from '@zooplus/ui-components';
import { useValidatableFormFieldFocus } from '../../../hooks';
import { TextFormFieldProps } from './types';
import { useFormFieldMainValidationWithOnValueChangeValidation } from '../../../hooks';
import { EmailFormFieldValidator } from '../../../hooks/validation/validator/EmailFormFieldValidator';

const EmailField: React.FC<TextFormFieldProps> = ({ externalValue }: any) => {
  const { t } = useTranslation();

  const label = t('form.fields.email.label');
  const placeholder = t('form.fields.email.placeholder');
  const minLength = null;
  const maxLength = 128;

  const [value, setValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    externalValue && setValue(externalValue);
  }, [externalValue]);

  const { hasError, errorTags, validate } = useFormFieldMainValidationWithOnValueChangeValidation(
    new EmailFormFieldValidator(true, minLength, maxLength),
    { value },
  );

  useEffect(() => {
    if (errorTags.includes('fieldAttributes')) {
      setErrorMessage(t('form.fields.email.error_messages.empty'));
    } else if (errorTags.includes('emailFormat')) {
      setErrorMessage(t('form.fields.email.error_messages.invalid'));
    }
  }, [errorTags]);

  const inputWrapperElementRef = useRef<HTMLDivElement>(null);
  useValidatableFormFieldFocus(
    { formFieldSelector: 'input', formFieldWrapperElementRef: inputWrapperElementRef },
    {},
  );

  const updateValueCallback = useCallback((evt) => setValue(evt.target.value), []);
  const validateCallback = useCallback(() => validate(), [validate]);

  return (
    <div>
      <div
        data-test="contactForm.field.email"
        className="contactForm__field"
        data-form-control-type="EMAIL">
        <FormFieldWrap
          labelFor="email"
          isRequired={true}
          labelText={label}
          errorMessage={hasError && errorMessage}
          ref={inputWrapperElementRef}>
          <Input
            hasError={hasError}
            onChange={updateValueCallback}
            inputProps={{
              id: 'email',
              name: 'email',
              placeholder: placeholder,
              type: 'text',
              readOnly: !!externalValue,
              maxLength: maxLength,
              value: value as string,
              onBlur: validateCallback,
            }}
          />
        </FormFieldWrap>
      </div>

      <div data-test="contactForm.field.secondaryEmail" className="secondaryEmail">
        <Input
          inputProps={{
            id: 'secondaryEmail',
            name: 'secondaryEmail',
            placeholder: 'Secondary Email',
            type: 'text',
            autoComplete: 'nope',
            'aria-hidden': 'true',
            tabIndex: -1,
            defaultValue: '',
            onBlur: validateCallback,
            maxLength,
          }}
        />
      </div>
    </div>
  );
};

export default EmailField;
